import { LogzioTheme } from '@logz-pkg/enums';
import { sessionStateService, sessionApiService, httpService, LoggerService } from '@logz-pkg/frontend-services';

const THEME_STORAGE_KEY = 'logzio-theme-preference';

type ThemeChangeListener = (theme: LogzioTheme) => void;

const themeChangeListeners: ThemeChangeListener[] = [];

const onThemeChange = (listener: ThemeChangeListener): (() => void) => {
  themeChangeListeners.push(listener);

  return () => {
    const index = themeChangeListeners.indexOf(listener);

    if (index !== -1) {
      themeChangeListeners.splice(index, 1);
    }
  };
};

const notifyThemeChange = (theme: LogzioTheme): void => {
  themeChangeListeners.forEach(listener => {
    try {
      listener(theme);
    } catch (e) {
      LoggerService.logError({ message: 'Error in theme change listener', error: e });
    }
  });
};

const getStoredTheme = (): LogzioTheme | null => {
  try {
    const storedThemeStr = localStorage.getItem(THEME_STORAGE_KEY);

    if (storedThemeStr !== null) {
      const storedTheme = Number(storedThemeStr);

      if (storedTheme === LogzioTheme.Light || storedTheme === LogzioTheme.Dark) {
        console.log('Using theme from localStorage:', storedTheme);

        return storedTheme;
      }
    }

    return null;
  } catch (e) {
    LoggerService.logError({ message: 'Failed to retrieve theme from localStorage', error: e });

    return null;
  }
};

const storeTheme = (theme: LogzioTheme): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme.toString());
  } catch (e) {
    LoggerService.logError({ message: 'Failed to store theme preference', error: e });
  }
};

const setTheme = async (theme: LogzioTheme): Promise<void> => {
  storeTheme(theme);

  try {
    await sessionStateService.setThemeWithoutSave(theme);
  } catch (e) {
    LoggerService.logError({ message: 'Failed to update session state', error: e });
  }

  notifyThemeChange(theme);

  try {
    const session = await sessionApiService.get();
    const currentSettings = session?.userSettings || {};

    await httpService.post('/user-settings', {
      ...currentSettings,
      kibanaTheme: theme,
      logzioTheme: theme,
    });

    sessionApiService.clearCache();
  } catch (e) {
    LoggerService.logError({ message: 'Failed to update user settings in backend', error: e });
  }
};

const initializeTheme = async (): Promise<LogzioTheme> => {
  try {
    const session = sessionStateService.session.get();

    if (session?.userSettings?.logzioTheme !== undefined) {
      const sessionTheme = session.userSettings.logzioTheme;

      storeTheme(sessionTheme);

      return sessionTheme;
    }
  } catch (e) {
    LoggerService.logError({ message: 'Failed to get theme from session', error: e });
  }

  const storedTheme = getStoredTheme();

  if (storedTheme !== null) return storedTheme;

  return LogzioTheme.Light;
};

export const themeService = {
  getStoredTheme,
  storeTheme,
  setTheme,
  initializeTheme,
  onThemeChange,
};
